// Pure geometry shared by native SVG, neutral annotation views and hit testing.
const EPS = 1e-9;
const sub = (a, b) => ({ x: a.x - b.x, z: a.z - b.z });
const add = (a, b) => ({ x: a.x + b.x, z: a.z + b.z });
const mul = (a, n) => ({ x: a.x * n, z: a.z * n });
const dot = (a, b) => a.x * b.x + a.z * b.z;
const cross = (a, b) => a.x * b.z - a.z * b.x;
const length = a => Math.hypot(a.x, a.z);
const unit = a => mul(a, 1 / length(a));
const finitePoint = p => p && Number.isFinite(p.x) && Number.isFinite(p.z);
export function validAngularSegment(segment) {
  return Boolean(finitePoint(segment?.a) && finitePoint(segment?.b)
    && length(sub(segment.b, segment.a)) > EPS);
}
function fallbackSegment(ref) {
  return ref?.type === 'segment' ? { a: ref.fallbackA, b: ref.fallbackB } : null;
}
function rayFromAnchor(center, anchor, segment) {
  const fromAnchor = sub(anchor, center);
  if (length(fromAnchor) > EPS) return unit(fromAnchor);
  const middle = sub(mul(add(segment.a, segment.b), 0.5), center);
  if (length(middle) > EPS) return unit(middle);
  return unit(sub(segment.b, center));
}
export function angularDimensionLayout(dimension, resolved = {}) {
  const segmentA = resolved.segmentA || fallbackSegment(dimension.refA);
  const segmentB = resolved.segmentB || fallbackSegment(dimension.refB);
  if (!validAngularSegment(segmentA) || !validAngularSegment(segmentB)) return null;
  const pointA = resolved.pointA || dimension.pointA;
  const pointB = resolved.pointB || dimension.pointB;
  if (!finitePoint(pointA) || !finitePoint(pointB)) return null;
  const directionA = unit(sub(segmentA.b, segmentA.a));
  const directionB = unit(sub(segmentB.b, segmentB.a));
  const determinant = cross(directionA, directionB);
  const radius = Math.max(1, Math.abs(Number(dimension.offset) || 120));
  const labelGap = 80; // Model-space clearance for the standard 30–60 mm plan labels.
  if (Math.abs(determinant) < EPS) {
    const center = mul(add(pointA, pointB), 0.5);
    const normal = { x: -directionA.z, z: directionA.x };
    return { angular: true, parallel: true, pointA, pointB, center, radius, labelGap, value: 0,
      axis: directionA, normal, dimA: pointA, dimB: pointB, arcPoints: [],
      lines: [[pointA, pointB]], textPosition: add(center, mul(normal, radius + labelGap)) };
  }
  const center = add(segmentA.a, mul(directionA, cross(sub(segmentB.a, segmentA.a), directionB) / determinant));
  if (!finitePoint(center) || Math.max(Math.abs(center.x), Math.abs(center.z)) > 1e9) return null;
  const rayA = rayFromAnchor(center, pointA, segmentA);
  const rayB = rayFromAnchor(center, pointB, segmentB);
  let sweep = Math.atan2(cross(rayA, rayB), dot(rayA, rayB));
  if (dimension.reflex === true) sweep += sweep > 0 ? -Math.PI * 2 : Math.PI * 2;
  const start = Math.atan2(rayA.z, rayA.x);
  const normal = { x: Math.cos(start + sweep / 2), z: Math.sin(start + sweep / 2) };
  // Bounded, deterministic polygonal arc for SVG captures and neutral renderers.
  const steps = Math.max(2, Math.ceil(Math.abs(sweep) / (Math.PI / 180)));
  const arcPoints = Array.from({ length: steps + 1 }, (_, i) => {
    const angle = start + sweep * i / steps;
    return add(center, { x: radius * Math.cos(angle), z: radius * Math.sin(angle) });
  });
  const dimA = arcPoints[0], dimB = arcPoints.at(-1);
  return { angular: true, parallel: false, pointA, pointB, center, radius, labelGap, start, sweep,
    value: Math.abs(sweep) * 180 / Math.PI, axis: { x: -normal.z, z: normal.x }, normal,
    dimA, dimB, arcPoints, textPosition: add(center, mul(normal, radius + labelGap)),
    lines: [[pointA, dimA], ...arcPoints.slice(1).map((p, i) => [arcPoints[i], p]), [pointB, dimB]] };
}

// The pointer chooses the sector between the clicked rays and its radius.
// Reused for placement, native label dragging and neutral annotation editing.
export function angularDimensionPlacement(layout, point) {
  const offset = Math.max(1, length(sub(point, layout.center)) - layout.labelGap);
  if (layout.parallel) return { offset, reflex: false, textPosition: null };
  const tau = Math.PI * 2;
  const minor = Math.abs(layout.sweep) > Math.PI
    ? layout.sweep + (layout.sweep > 0 ? -tau : tau) : layout.sweep;
  const pointerAngle = Math.atan2(point.z - layout.center.z, point.x - layout.center.x);
  const relative = ((pointerAngle - layout.start) * (minor < 0 ? -1 : 1) % tau + tau) % tau;
  return { offset, reflex: relative > Math.abs(minor) + 1e-9, textPosition: null };
}
